import {
  type AnyRecord,
  DIALOGUE_EVENT_TYPES,
  REVIEW_STATES,
  type RecordApiKind,
  type ReviewState,
} from '@fruuit/shared'
import { formatDate } from './format'

/** Singular nouns for record kinds, used in titles and empty states. */
export const RECORD_KIND_NOUNS: Record<RecordApiKind, string> = {
  issuers: 'issuer',
  securities: 'security',
  sources: 'evidence source',
  incidents: 'incident',
  assessments: 'assessment',
  engagements: 'engagement',
  objectives: 'objective',
  milestones: 'evidence review',
  dialogues: 'correspondence record',
}

/** A short human label for any record (pickers, chips, drawers). */
export function recordLabel(record: AnyRecord): string {
  switch (record.kind) {
    case 'issuers':
      return record.legal_name
    case 'securities':
      return `${record.identifier_type} ${record.identifier_value}${record.name ? ` · ${record.name}` : ''}`
    case 'assessments':
      return `${record.issuer_name ?? 'Assessment'} · v${record.version}`
    case 'milestones':
      return `${record.stage} · ${formatDate(record.assessment_date)}`
    case 'dialogues':
      return `${record.title} · ${(DIALOGUE_EVENT_TYPES.labels as Record<string, string>)[record.event_type] ?? record.event_type}`
    default:
      return record.title
  }
}

export function reviewStateLabel(state: ReviewState | string): string {
  return (REVIEW_STATES.labels as Record<string, string>)[state] ?? state
}

/** Explains whether a reviewed record can still be edited (WF-009). */
export function editableExplanation(state: ReviewState): string {
  switch (state) {
    case 'draft':
      return 'Draft: contributors can edit it. Submit it for an independent review when it is ready.'
    case 'returned':
      return 'Returned for changes: edit the draft and submit it again.'
    case 'in_review':
      return 'In review: the content is locked while an independent reviewer checks it.'
    case 'approved':
      return 'Approved: the content is locked. A reviewer can publish it or return it for changes.'
    case 'published':
      return 'Published: this version is immutable. Create a corrected version to change it.'
    case 'retracted':
      return 'Retracted: withdrawn from client view. The record stays in the history and audit trail.'
  }
}

export const EDITABLE_STATES: readonly ReviewState[] = ['draft', 'returned']
